'use client';

import { CheckIcon, ChevronDownIcon, LanguagesIcon } from 'lucide-react';

import { Link, useHrefOptions, useTranslations } from '@northladder/i18n';
import { useRegionStore } from '@northladder/services';
import { languagesConfig, isLangRTL } from '@northladder/utilities';

import { cn } from '../../lib';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
} from '../../ui';

/**
 * -----------------------------------------------------------------------------
 * Renders a dropdown menu to switch the language of the application.
 */
export function LanguageSwitchWidget() {
  const t = useTranslations('DesignSystemComponents.LanguageWidget');
  const { activeLanguage, locale: activeLocale, href } = useHrefOptions();
  const { tenantConfig, updateAppLanguage } = useRegionStore();
  const availableLanguages = tenantConfig?.commonAppConfig?.locales ?? [];

  const filteredLanguages =
    availableLanguages.length > 0
      ? languagesConfig.filter(({ id }) => availableLanguages.includes(id))
      : languagesConfig;

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              className="
                transition-all duration-300
                group-data-[state=collapsed]:justify-center
                group-data-[state=expanded]:space-x-2.5
              "
              size="lg"
            >
              <LanguagesIcon className="size-4 shrink-0 stroke-[1.25]" />

              <span className="block text-start group-data-[state=collapsed]:hidden">
                <span className="line-clamp-none block text-sm/5 font-normal">
                  {t('label')}
                </span>
                <span className="text-text-caption block text-xs/5">
                  {activeLanguage.label}
                </span>
              </span>
              <ChevronDownIcon
                className="
                  size-4 ms-auto shrink-0 group-data-[state=collapsed]:hidden
                "
              />
            </SidebarMenuButton>
          </DropdownMenuTrigger>

          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel className="text-text-caption">
              {t('instruction')}
            </DropdownMenuLabel>

            {filteredLanguages.map(({ id, label, locale }) => {
              const isActive = locale === activeLocale;
              const localeFontClass = isLangRTL(locale)
                ? 'font-primary-ar'
                : '';

              const handleUpdateLanguage = () => {
                if (!isActive) {
                  updateAppLanguage(id);
                }
              };

              return (
                <DropdownMenuItem key={id} asChild>
                  <Link
                    className={cn(localeFontClass)}
                    data-selected={isActive}
                    href={href}
                    locale={locale}
                    onClick={handleUpdateLanguage}
                  >
                    <LanguagesIcon className="text-inherit" />
                    {label}
                    {isActive ? (
                      <CheckIcon className="ms-auto text-inherit" />
                    ) : null}
                  </Link>
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
